import _ from 'lodash';
import * as members from 'privates/privateMembers';
import * as postMessage from 'privates/postMessage';
import platformUtils from 'platformUtils';
import {validate} from 'privates/validations/validation';
import reporter from 'privates/reporter';
import {generateError} from 'privates/validations/errors';


/**
 * @namespace wix-site
 * @summary The wix-site module contains functionality for obtaining information about your site and its pages.
 * @description
 *  To use the site module, import `wixSite` from the `wix-site` module:
 *
 *    ```javascript
 *    import wixSite from 'wix-site';
 *    ```
 *
 *  The APIs in `wix-site` can only be used in front-end code.
 */

/**
 * @typedef {external:Object} wix-site~SiteStructure
 * @summary An object that contains information about the site's pages, prefixes, and lightboxes.
 * @description
 *  The `SiteStructure` object is a flattened representation of the site. Use
 *  the [`getSiteStructure()`](#getSiteStructure) function to get the site's
 *  `SiteStructure` object. The structure does not include the headers and links
 *  from the site's **Pages** menu.
 * @snippet [Site-getSiteStructure.es6=Get information about the site's pages, prefixes, and lightboxes]
 *  This example gets the structure of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {wix-site~StructurePage[]} pages The pages of the site.
 * @property {wix-site~Prefix[]} prefixes The prefixes of the site's routers and dynamic pages.
 * @property {wix-site~StructureLightbox[]} lightboxes The site's lightboxes.
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~StructurePage
 * @summary An object that contains information about a page on the site.
 * @description
 *  The `StructurePage` object represents a page in the site. The page can be a
 *  regular page, dynamic page, router page, or page from an app.
 * @snippet [Site-pages.es6=Get information about the site's pages]
 *  This example gets the pages of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The of the page.
 * @property {external:String} type The type of the page: `"static"` or `"template"`.
 * @property {external:String} [url] The URL of the page. If the page is a `static` page the `url` property is the page's SEO URL.
 * @property {external:String} [applicationId] If the page is an app page, the `applicationId` property is the app's ID.
 * @property {external:String} [prefix] If the page is a router or dynamic page, the `prefix` property is the page's prefix.
 * @property {external:Boolean} [isHomePage] If the page is the Home page, `isHomePage` is `true`. Otherwise, it does not exist.
 * @see [SiteStructure](#SiteStructure)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~Prefix
 * @summary An object that contains information about the site's prefixes.
 * @description
 *  A site's routers, dynamic pages, and app pages all have prefixes.
 * @snippet [Site-prefixes.es6=Get information about the site's prefixes]
 *  This example gets the prefixes of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The name of the prefix.
 * @property {external:String} type The type of the prefix: `"dynamicPages"`, `"router"`, or `"app"`.
 * @property {external:String} prefix The prefix.
 * @property {external:String} [applicationId] If the prefix is an app's prefix, the `applicationId` property is the app's ID.
 * @see [SiteStructure](#SiteStructure)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~StructureLightbox
 * @summary An object that contains information about a lightbox on the site.
 * @snippet [Site-lightboxes.es6=Get information about the site's lightboxes]
 *  This example gets the lightboxes of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The name of the lightbox.
 * @see [SiteStructure](#SiteStructure)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */




class Site {

    constructor(appDefId){
        members._appDefId.set(this, appDefId);
    }

    /**
     * @function routerSitemap
     * @syntax
     * function routerSitemap(routerPrefix: string): Promise<WixRouterSitemapEntry[]>
     * @summary Returns the sitemap for a router or dynamic page prefix.
     * @description
     *  The `routerSitemap()` function returns a Promise that resolves to a list of
     *  [`WixSitemapEntry`](wix-router.WixRouterSitemapEntry.html) objects. Each
     *  [`WixSitemapEntry`](wix-router.WixRouterSitemapEntry.html) includes
     *  information about a page, such as its URL, title, and name.
     *
     *  A sitemap is used by search engines to find the links to the site's pages.
     * @snippet [Site-routerSiteMap.es6=Get the sitemap for a prefix]
     * @param {external:String} routerPrefix The prefix of the router to get the sitemap for.
     * @returns {external:Promise}
     * @fulfill {wix-router.WixRouterSitemapEntry[]} A list of sitemap entries.
     * @reject {external:Error} The error that caused the rejection.
     * @memberof wix-site
     */
    routerSitemap(prefix) {
        return new Promise((resolve, reject) => {
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.GET_ROUTERS_SITEMAP,
                data: {prefix},
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function getSiteStructure
     * @syntax
     * function wixSite.getSiteStructure(): SiteStructure
     * @summary Returns information about the site's pages, prefixes, and lightboxes.
     * @snippet [Site-getSiteStructure.es6=Get information about the site's pages, prefixes, and lightboxes]
     *  This example gets the structure of the site with the following in its pages menu:
     *  ![Site Structure](images/site_structure.png "Site Structure")
     * @returns {wix-site~SiteStructure} An object that contains information about the site's pages, prefixes, and lightboxes.
     * @memberof wix-site
     */
    getSiteStructure() {
        const RMI = platformUtils.getRMI('wix-site.getSiteStructure');
        const siteStructure = RMI.getSiteStructure();
        _.map(siteStructure, (val, key) => {
            siteStructure[key] = _.map(val, (obj) => {
                if (obj.isMainPage) {
                    obj.isHomePage = obj.isMainPage;
                }
                return _.omit(obj, ['id', 'isMainPage', 'tpaPageId']);
            });
        });
        return siteStructure;

    }

    getAppToken(appDefinitionId) {
        const isValid = validate({
            compType: 'Site',
            functionName: 'getAppToken',
            dataToValidate: {appDefinitionId}
        });
        if (!isValid) {
            return;
        }
        const appData = platformUtils.getRGI().getAppData(appDefinitionId);
        if (appData) {
            return appData.instance;
        }
        reporter.reportSdkError(generateError.error_app_does_not_exist({
            appDefId: appDefinitionId
        }));
    }
}

export default Site;



// WEBPACK FOOTER //
// ./js/modules/namespaces/site.es6